import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MessageCircle, ChevronRight, User } from 'lucide-react';
import useMessagesStore from '@/stores/useMessagesStore';

import { dateLocale } from '@/utils/dateLocale';
import { memberLabel } from '@/utils/memberLabel';
import { isRtl } from '@/i18n/config';

/**
 * MessageBell Component
 *
 * Displays a message icon with unread count badge and dropdown.
 * Shows user-to-user conversations.
 */
const MessageBell = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Conversations from messages store
  const conversations = useMessagesStore(state => state.conversations);
  const msgUnreadCount = useMessagesStore(state => state.unreadCount);
  const unreadByConversation = useMessagesStore(state => state.unreadByConversation);
  const loading = useMessagesStore(state => state.loading);

  // Unread messages, cleared chat by chat as each is READ - never by opening
  // this menu, which also wiped the highlight showing WHICH chats were unread.
  const totalUnread = msgUnreadCount;

  const handleConversationClick = (conversationId) => {
    setIsOpen(false);
    navigate(`/messages?conversation=${conversationId}`);
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const time = new Date(timestamp);
    if (isNaN(time.getTime())) return '';

    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('bells.timeNow');
    if (diffMins < 60) return t('bells.timeMinutesShort', { count: diffMins });
    if (diffHours < 24) return t('bells.timeHoursShort', { count: diffHours });
    if (diffDays < 7) return t('bells.timeDaysShort', { count: diffDays });
    return time.toLocaleDateString(dateLocale());
  };

  if (!isAuthenticated) {
    return null;
  }

  // The menu takes the PAGE's direction. Radix defaults to ltr, so both
  // menus were mirrored by hand for Arabic and read backwards in every
  // left-to-right language (2026-10-30).
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen} dir={isRtl(i18n.language) ? 'rtl' : 'ltr'}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full relative h-9 w-9 md:h-10 md:w-10"
          aria-label={totalUnread > 0 ? t('bells.messagesAriaUnread', { count: totalUnread }) : t('bells.messagesAria')}
        >
          <MessageCircle className="h-4 w-4 md:h-5 md:w-5" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
              {totalUnread > 9 ? '9+' : totalUnread}
            </span>
          )}
          <span className="sr-only">
            {totalUnread > 0 ? t('bells.unreadMessages', { count: totalUnread }) : t('bells.noUnreadMessages')}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="px-4 py-3 border-b">
          <h3 className="font-bold text-wefaqna-navy text-lg text-start">{t('bells.messagesTitle')}</h3>
        </div>

        <ScrollArea className="max-h-[300px]">
          {loading && conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {t('bells.loading')}
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>{t('bells.noConversations')}</p>
            </div>
          ) : (
            conversations.slice(0, 5).map((conv) => {
              const hasUnread = (unreadByConversation[conv.id] || 0) > 0;
              return (
                <DropdownMenuItem
                  key={conv.id}
                  className={`p-3 cursor-pointer hover:bg-wefaqna-subtle focus:bg-wefaqna-subtle ${hasUnread ? 'bg-blue-50' : ''}`}
                  onClick={() => handleConversationClick(conv.id)}
                >
                  <div className="flex items-start gap-3 w-full">
                    {hasUnread && (
                      <div className="w-2 h-2 bg-wefaqna-green rounded-full mt-2 flex-shrink-0" />
                    )}
                    <div className="bg-wefaqna-green/10 rounded-full p-2 flex-shrink-0">
                      <User className="h-4 w-4 text-wefaqna-forest" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-medium text-sm truncate ${hasUnread ? 'text-wefaqna-navy font-bold' : 'text-gray-700'}`}>
                          {conv.is_admin_chat ? t('chat.adminChat') : memberLabel(conv, t('bells.conversationFallback'))}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {getTimeAgo(conv.last_message_at)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {t('bells.tapToView')}
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })
          )}
        </ScrollArea>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="p-3 cursor-pointer justify-center text-wefaqna-forest hover:text-wefaqna-green-hover font-medium"
          onClick={() => {
            setIsOpen(false);
            navigate('/messages');
          }}
        >
          <span>{t('bells.viewAllMessages')}</span>
          <ChevronRight className="h-4 w-4 ms-1 rtl:rotate-180" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MessageBell;
